// Sign Out. With the session kept in localStorage (oidcConfig.js), closing
// the browser no longer signs anyone out, so this must clear it explicitly:
//
//   0. raise the signing-out flag and stop background silent renew - the
//      components that auto-start sign-in when they see no session
//      (MemberLogin, PaymentSuccess, the /members guard) check the flag, so
//      none of them can race the Cognito logout and sign the user back in,
//      and no in-flight renew can write the user back,
//   1. revoke the refresh token at Cognito (best effort - a stolen copy
//      stops working),
//   2. remove the stored user from localStorage (and sweep any oidc.user:*
//      entries left behind if the library call fails),
//   3. forget silent sign-in attempts,
//   4. end the Cognito session (its /logout clears the SSO cookie, so the
//      next /members visit goes to /signup instead of signing back in).
import { COGNITO } from './oidcConfig.js';
import { clearSsoAttempt } from './silentSignIn.js';

const STORED_USER_PREFIX = 'oidc.user:';

// Lives until the page unloads for Cognito's /logout, which is the point.
let signingOut = false;

export const isSigningOut = () => signingOut;

/** Test-only: module state persists between tests in one file. */
export const resetSignOutForTests = () => {
  signingOut = false;
};

export const cognitoLogoutUrl = () =>
  `${COGNITO.domain}/logout?client_id=${encodeURIComponent(COGNITO.clientId)}` +
  `&logout_uri=${encodeURIComponent(COGNITO.logoutUri)}`;

const sweepStoredUsers = () => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(STORED_USER_PREFIX))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was persisted there.
  }
};

/**
 * @param {{ revokeTokens: (types?: string[]) => Promise<void>, removeUser: () => Promise<void>,
 *          stopSilentRenew?: () => void }} auth
 *   the react-oidc-context auth object
 * @param {{ navigate?: (url: string) => void, log?: (...args: unknown[]) => void }} [options]
 */
export const signOut = async (auth, { navigate = (url) => window.location.assign(url), log = console.error } = {}) => {
  signingOut = true;
  try {
    auth.stopSilentRenew?.();
  } catch (err) {
    log('Stopping silent renew failed:', err?.message);
  }
  try {
    await auth.revokeTokens(['refresh_token']);
  } catch (err) {
    log('Refresh token revocation failed:', err?.message);
  }
  try {
    await auth.removeUser();
  } catch (err) {
    log('Removing the stored session failed:', err?.message);
  }
  sweepStoredUsers();
  clearSsoAttempt();
  navigate(cognitoLogoutUrl());
};
